import React, { forwardRef, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { ButtonText, InputProps, SearchInput } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { fetchExperts } from 'src/api/queries';
import { IUser } from '@publishing-engine/domain/models';
import { MakeReadOnly } from './AddExperts';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import SubmittedAuthorDetailsModal from '../../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';

interface Props {
  onExpertsSelect: (boardMemberId: IUser) => void;
  onInputClear: () => void;
  isReadOnlyOption: MakeReadOnly;
  index: number;
  selectedExpert: string;
}

const ExpertsSearch = forwardRef<HTMLInputElement, InputProps & Props>(
  (
    {
      onExpertsSelect,
      onInputClear,
      isReadOnlyOption,
      selectedExpert,
      index,
      ...props
    },
    ref
  ) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedUserId, setSelectedUserId] = React.useState<string | null>(
      null
    );
    const [searchValue, setSearchValue] = useState('');
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value); // keep it in sync
      onOpen(); // Show the filtered list dynamically
      return props?.onChange?.(e);
    };
    const { submissionContextId } = useSubmissionData();
    const { data: expertsList, isLoading } = useQuery({
      queryKey: ['board-member-experts', { searchValue, submissionContextId }],
      queryFn: () =>
        submissionContextId
          ? fetchExperts(searchValue, submissionContextId)
          : null,
      enabled: !!submissionContextId
    });

    const renderExpertsList = () => {
      // loading state
      if (isLoading) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <Spinner fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">Loading...</Text>
              <Text textStyle="textBody" paddingX={2}>
                We are currently fetching Board Member Results.
              </Text>
            </Flex>
          </Flex>
        );
      }
      // empty data state
      if (!isLoading && expertsList?.response.length === 0) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <FontAwesomeIcon icon={faWarning} fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">No Results</Text>
              <Text textStyle="textBody" paddingX={2}>
                We could not find the Expert you’re looking for.
              </Text>
            </Flex>
          </Flex>
        );
      }

      return (
        <>
          {selectedUserId ? (
            <SubmittedAuthorDetailsModal
              backButtonText="Back to Invite Experts"
              userId={selectedUserId ?? ''}
              onBackClick={() => setSelectedUserId(null)}
            />
          ) : null}

          <List
            padding={'0 !important'}
            display="flex"
            flexDirection="column"
            gap={4}
          >
            {expertsList?.response?.map((boardMember, index) => {
              return (
                <ListItem cursor="pointer" key={boardMember.id}>
                  <>
                    <Flex alignItems="center" padding={2}>
                      <Text textStyle="headingItem" flex={1}>
                        {boardMember.firstName} {boardMember.lastName}
                      </Text>
                      <Box flex={1}>
                        <ButtonText
                          size="regular"
                          iconLeft={
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          }
                          onClick={() => setSelectedUserId(boardMember?.id)}
                        >
                          View Profile
                        </ButtonText>
                      </Box>
                      <Box flex={1}>
                        <ButtonText
                          onClick={() => {
                            onExpertsSelect(boardMember);
                            onClose();
                            setSearchValue(
                              `${boardMember.firstName} ${boardMember.lastName}`
                            );
                          }}
                        >
                          Select Expert
                        </ButtonText>
                      </Box>
                    </Flex>
                    {expertsList?.response?.length - 1 !== index && <Divider />}
                  </>
                </ListItem>
              );
            })}
          </List>
        </>
      );
    };

    return (
      <Popover
        returnFocusOnClose={true}
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom"
        closeOnBlur={true}
        matchWidth
        autoFocus={false}
      >
        <PopoverTrigger>
          <SearchInput
            ref={ref}
            {...props}
            onChange={handleChange}
            onInputClear={onInputClear}
            value={searchValue || selectedExpert}
            isReadOnly={
              isReadOnlyOption.readOnly && isReadOnlyOption.index === index
            }
            data-cy="experts-search-input"
          />
        </PopoverTrigger>
        <PopoverContent
          minW={{ base: '100%' }}
          paddingY={4}
          paddingX={2}
        >
          <PopoverBody padding={0}>{renderExpertsList()}</PopoverBody>
        </PopoverContent>
      </Popover>
    );
  }
);

export default ExpertsSearch;
